import styled, { css } from 'styled-components'
import { border1, surface2, warmTint, white } from '@/theme/colors'
import { motion, radii, shadows, space } from '@/theme/tokens'

/** The resting card: white, 16px, warm `shadows.card`. */
export const cardSurface = css`
	background: ${white};
	border-radius: ${radii.card};
	box-shadow: ${shadows.card};
	padding: ${space[6]};
`

/**
 * Lift on hover — only when the whole card is the control (ADR 0008). Touch
 * devices get the press alone, since a hover lift sticks after a tap.
 */
export const liftOnHover = css`
	cursor: pointer;
	transition:
		box-shadow ${motion.base},
		transform ${motion.base};

	&:hover {
		box-shadow: ${shadows.cardHover};
		transform: translateY(-2px);
	}

	&:active {
		transform: scale(0.98);
	}

	@media (hover: none) {
		&:hover {
			box-shadow: ${shadows.card};
			transform: none;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		transition: none;

		&:hover {
			transform: none;
		}
	}
`

/**
 * A row or tile nested inside a card: a flat `surface2` tint on the white card
 * ground, 12px, no shadow and no border. Tint, not elevation, marks the tier.
 */
export const innerCardSurface = css`
	background: ${surface2};
	border-radius: ${radii.inner};
	padding: ${space[3]} ${space[4]};
`

/**
 * Hover for an inner card that is the control: the tint deepens one step to
 * `warmTint`, the way a button signals hover with colour — no lift, no shadow.
 * Touch devices keep the resting tint, since a hover state sticks after a tap.
 */
export const tintOnHover = css`
	cursor: pointer;
	transition: background-color ${motion.fast};

	&:hover {
		background: ${warmTint};
	}

	@media (hover: none) {
		&:hover {
			background: ${surface2};
		}
	}
`

/** A flat panel for tables and form groups: hairline border, no shadow. */
export const panelSurface = css`
	background: ${white};
	border-radius: ${radii.card};
	border: 1px solid ${border1};
	padding: ${space[6]};
`

export const Card = styled.div<{ $interactive?: boolean }>`
	${cardSurface}
	${({ $interactive }) => ($interactive ? liftOnHover : '')}
`

export const InnerCard = styled.div`
	${innerCardSurface}
`

export const Panel = styled.div`
	${panelSurface}
`
